import { useState, useEffect } from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from 'react-router-dom';
import {
  SidePanel,
  ButtonPseudo,
  SidePanelLoader,
  SidePanelType,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import Form from './agreement-form';
import { CustomerType } from '../types';
import {
  useLazyGetCustomerAgreementQuery,
  useLazyGetCustomerAgreementFileQuery,
} from '../store/services/customers';
import { clearAgreementState } from '../store/slices/documents/documents';
import getAgreementTypeName from '../../new-application/helpers/get-agreement-type-name';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const CustomerAgreement = ({
  customerType,
}: {
  customerType: CustomerType;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [link, setLink] = useState('');

  const { agreement } = useAppSelector((state) => state.customerDocuments);

  const [getAgreement, { isLoading, isFetching }] =
    useLazyGetCustomerAgreementQuery();
  const [getFile] = useLazyGetCustomerAgreementFileQuery();

  const { agreementId, customerId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();

  const getAgreementFile = async (id: string) => {
    const response = await getFile(id);

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setLink(blob);
    }
  };

  const dispatch = useAppDispatch();

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        `${generatePath(
          customerType === CustomerType.INDIVIDUAL
            ? ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS
            : ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS,
          { customerId: (customerId as string) || '' },
        )}${search || ''}`,
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    if (agreementId) {
      getAgreement(agreementId);
      getAgreementFile(agreementId);
    }

    return () => {
      dispatch(clearAgreementState());
    };
  }, [agreementId]);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        {agreement?.uploadId && (
          <div className={cn.file}>
            <div
              className={`${cn.fileName} ${
                agreementId ? cn.fileNameFullWidth : ''
              }`}
            >
              {agreement && link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                >{`${getAgreementTypeName(
                  agreement?.type,
                )} (v${agreement.version})`}</a>
              )}
              {!link && 'Loading...'}
            </div>
          </div>
        )}
        <div className={cn.agreementFormSeparator} />
        <Form agreement={agreement} agreementId={agreementId} />
      </>
    );
  }

  let title;

  if (agreement) {
    title = getAgreementTypeName(agreement?.type);
  } else {
    title = 'Agreement';
  }

  return (
    <SidePanel
      type={SidePanelType.AML}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={handleClose}
      title={title}
      footer={<ButtonPseudo text="Close" onClick={handleClose} bold />}
      sidepanelExpandedKey="customers-agreements"
    >
      {content}
    </SidePanel>
  );
};

export default CustomerAgreement;
